import { Link } from 'react-router-dom';
import { Facebook, Users, Trophy, Gamepad2, Medal, UsersRound, Mail, FileText, Shield, ChevronRight } from 'lucide-react';
import vsuLogo from '@/assets/vsu-logo.jpg';

const quickLinks = [
  { to: '/players', label: 'Players', icon: Users },
  { to: '/tournaments', label: 'Tournaments', icon: Trophy },
  { to: '/matches', label: 'Matches', icon: Gamepad2 },
  { to: '/leaderboard', label: 'Leaderboard', icon: Medal },
  { to: '/squads', label: 'Squads', icon: UsersRound },
];

const infoLinks = [
  { to: '/contact', label: 'Contact Us', icon: Mail },
  { to: '/terms', label: 'Terms of Service', icon: FileText },
  { to: '/privacy', label: 'Privacy Policy', icon: Shield },
];

export function Footer() {
  return (
    <footer className="relative border-t border-primary/20 bg-gradient-to-b from-background via-background to-black/50 mobile-safe-bottom pb-0 md:pb-0 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>
      
      <div className="container relative py-8 md:py-16 px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="group flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary via-cyan-500 to-primary rounded-xl blur opacity-40 group-hover:opacity-60 transition-opacity" />
                <img 
                  src={vsuLogo} 
                  alt="Viper Strike United Logo" 
                  className="relative h-10 w-10 md:h-14 md:w-14 rounded-xl object-cover ring-2 ring-primary/30"
                />
              </div>
              <div>
                <span className="font-display text-base md:text-2xl tracking-wider bg-gradient-to-r from-white via-primary to-cyan-400 bg-clip-text text-transparent block font-bold">
                  Viper Strike United
                </span>
                <span className="text-[10px] md:text-xs text-primary/80 uppercase tracking-[0.2em] font-medium">
                  eFootball Mobile Club
                </span>
              </div>
            </Link>
            <p className="text-muted-foreground text-xs md:text-sm max-w-sm leading-relaxed hidden md:block mb-6">
              Bangladesh's premier eFootball mobile gaming community. 
              Compete, improve, and dominate the virtual pitch.
            </p>
            
            {/* Social Links */}
            <a 
              href="https://www.facebook.com/share/1ZqTxJbS2m/" 
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
            >
              <Facebook className="h-4 w-4" />
              <span className="text-xs font-medium">Follow Us</span>
              <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm md:text-lg mb-4 md:mb-5 text-foreground flex items-center gap-2">
              <span className="w-1 h-4 bg-gradient-to-b from-primary to-cyan-500 rounded-full" />
              Quick Links
            </h4>
            <ul className="space-y-1.5 md:space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center gap-2 text-xs md:text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                  >
                    <link.icon className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary/50 group-hover:text-primary transition-colors" />
                    <span>{link.label}</span>
                    <ChevronRight className="h-3 w-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Links */}
          <div>
            <h4 className="font-display text-sm md:text-lg mb-4 md:mb-5 text-foreground flex items-center gap-2">
              <span className="w-1 h-4 bg-gradient-to-b from-cyan-500 to-primary rounded-full" />
              Info
            </h4>
            <ul className="space-y-1.5 md:space-y-2">
              {infoLinks.map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="group flex items-center gap-2 text-xs md:text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                  >
                    <link.icon className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary/50 group-hover:text-primary transition-colors" />
                    <span>{link.label}</span>
                    <ChevronRight className="h-3 w-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-primary/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 text-[10px] md:text-sm text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span>© {new Date().getFullYear()} Viper Strike United. All rights reserved.</span>
          </div>
          <div className="text-center text-[10px] md:text-xs text-muted-foreground">
            <span>Powered by </span>
            <span className="text-primary font-medium">(Bhubon)</span>
            <span> Technical Head of VSU</span>
          </div>
          <div className="flex items-center gap-3 md:gap-6">
            {['Terms', 'Privacy', 'Contact'].map((item, i) => (
              <Link 
                key={item}
                to={`/${item.toLowerCase()}`} 
                className="text-[10px] md:text-sm text-muted-foreground hover:text-primary transition-colors relative group"
              >
                {item}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-primary group-hover:w-full transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
